/*
 * Whether Field Mode's camera asks the AI by itself after Save (the
 * post-capture summary, components/field/FieldCaptureSummary.js), per person
 * and per device. On unless turned off: only "off" is kept, as
 * {[userId]: false} under one key, so a phone shared by two collectors keeps
 * each one's choice. Kept in localStorage like utils/activeTarget.js; a
 * storage that refuses (a private window) means "on", and a change it could
 * not keep still holds for this page.
 *
 * A `storage` event never reaches the tab that wrote it, so a change is also
 * announced on `window` (CHANGE_EVENT).
 */
import { useEffect, useState } from 'react';

export const FIELD_AUTO_AI_KEY = 'mb_field_auto_ai';
export const CHANGE_EVENT = 'pitriot:field-auto-ai';

// What this page was told when the storage would not keep it.
const unkept = new Map();

function storageOf(storage) {
  if (storage) return storage;
  try { return globalThis.localStorage; } catch { return null; }
}

function readMap(store) {
  try {
    const raw = store?.getItem(FIELD_AUTO_AI_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
  } catch {
    return {};
  }
}

/** On, unless this person turned it off on this device. */
export function readFieldAutoAi(userId, storage) {
  const key = String(userId ?? '');
  if (unkept.has(key)) return unkept.get(key);
  return readMap(storageOf(storage))[key] !== false;
}

export function setFieldAutoAi(userId, on, storage) {
  const key = String(userId ?? '');
  const store = storageOf(storage);
  const map = readMap(store);
  if (on) delete map[key];
  else map[key] = false;
  try {
    if (Object.keys(map).length) store.setItem(FIELD_AUTO_AI_KEY, JSON.stringify(map));
    else store.removeItem(FIELD_AUTO_AI_KEY);
    unkept.delete(key);
  } catch {
    unkept.set(key, !!on);
  }
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: key }));
  } catch {
    /* no events here: the next read finds it */
  }
}

/** [on, setOn] for this person, following changes in this tab and others. */
export function useFieldAutoAi(userId) {
  const [on, setOn] = useState(() => readFieldAutoAi(userId));
  useEffect(() => {
    const refresh = () => setOn(readFieldAutoAi(userId));
    refresh();
    const onStorage = (e) => { if (!e || e.key === null || e.key === FIELD_AUTO_AI_KEY) refresh(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, [userId]);
  return [on, (value) => setFieldAutoAi(userId, value)];
}

/** For tests: forget what this page held for a storage that refused. */
export function forgetUnkeptFieldAutoAi() {
  unkept.clear();
}
